import { domainLookupKeys, hostFromUrl } from './activity-domain.js';
export const CLASSIFICATION_AUDIENCES = ['doer', 'staff'];
/** `doer` role rates against the doer set; every other role against staff.
 *  A user that is not found (null/undefined role) is treated as a doer. */
export function audienceForRole(role) {
    return role === 'doer' || role == null ? 'doer' : 'staff';
}
/** `lower(trim(executable))` when non-blank, else `lower(trim(app_name))`.
 *  MUST match the SQL in `compute_agent_activity_daily_rollup`. */
export function normaliseAppKey(executable, appName) {
    const exe = executable?.trim();
    const raw = exe ? exe : appName?.trim();
    return raw ? raw.toLowerCase() : null;
}
/** Windows Store (UWP) apps are hosted in this process, and the agent reports
 *  it instead of the app (Media Player, Calculator, Settings, ...). ActivTrak
 *  reports the hosted executable, so the platform recovers the app from the
 *  window title until the agent does (ClickUp 14zf25zk209). */
export const FRAME_HOST_APP_KEY = 'applicationframehost.exe';
/** Postgres `btrim(s)` trims spaces only, and the SQL twin must agree.
 *
 *  Known gap, accepted: `normaliseAppKey`, which the admin alias and classify
 *  routes use, trims all whitespace with JS `.trim()`. A title ending in a tab,
 *  newline or non-breaking space would therefore key as `...|photos\t`, while an
 *  admin's alias for it would be stored as `...|photos` and never match. No
 *  frame host title in production or on stage had such a character
 *  (2026-10-14). Matching `.trim()` exactly would need the same long character
 *  list kept in step in SQL and TS, so it waits until a real title needs it. */
const trimSpaces = (s) => s.replace(/^ +| +$/g, '');
/** The app part of a frame host's title: direction marks (U+200E/U+200F)
 *  removed, the text after the last ' - ' (a document name comes first, e.g.
 *  'IMG_1234.jpg - Photos'), or the whole title when that is blank; lowercased.
 *  Null for a blank title. MUST match `compute_agent_activity_daily_rollup`. */
export function frameHostTitlePart(title) {
    const t = trimSpaces((title ?? '').replace(/[\u200E\u200F]/g, ''));
    if (!t)
        return null;
    const i = t.lastIndexOf(' - ');
    const last = i >= 0 ? trimSpaces(t.slice(i + 3)) : t;
    return (last || t).toLowerCase();
}
/** The one key a desktop `foreground_app` row is classified by: its domain when
 *  the URL has an http(s) host, otherwise its app. The browser never decides.
 *  A frame host with a title is keyed `applicationframehost.exe|<title part>`,
 *  which an admin maps to the real app through `activity_app_aliases`; with no
 *  alias it reads Undefined rather than inheriting the frame host's rating. */
export function resolveEventKey(row) {
    const host = hostFromUrl(row.url);
    if (host)
        return { key_type: 'domain', key_value: host };
    const app = normaliseAppKey(row.executable, row.app_name);
    if (!app)
        return null;
    const titlePart = app === FRAME_HOST_APP_KEY ? frameHostTitlePart(row.window_title) : null;
    return { key_type: 'app', key_value: titlePart ? `${app}|${titlePart}` : app };
}
/** Decision 4: a browser is never classified itself. Its time counts by the
 *  site when the row has an http(s) URL; without one it is Undefined and never
 *  queued. Windows image names (the same list as the doer-apps URL-capture
 *  allowlist, 86d4c2k75), macOS app names (today's `app_name`), and macOS bundle
 *  ids (what `executable` carries once 14zf25zhzkr ships), as normalised app
 *  keys. */
export const BROWSER_APP_KEYS = new Set([
    'chrome.exe',
    'msedge.exe',
    'brave.exe',
    'firefox.exe',
    'opera.exe',
    'vivaldi.exe',
    'google chrome',
    'microsoft edge',
    'brave browser',
    'firefox',
    'safari',
    'opera',
    'vivaldi',
    'arc',
    'com.google.chrome',
    'com.microsoft.edgemac',
    'com.brave.browser',
    'org.mozilla.firefox',
    'com.apple.safari',
    'company.thebrowser.browser',
    'com.operasoftware.opera',
    'com.vivaldi.vivaldi',
]);
/** Takes the canonical key, i.e. after alias resolution. */
export function isBrowserAppKey(key) {
    return BROWSER_APP_KEYS.has(key);
}
export function buildClassificationIndex(entries, aliases) {
    const apps = new Map();
    const domains = new Map();
    for (const e of entries)
        (e.key_type === 'app' ? apps : domains).set(e.key_value, e);
    const aliasMap = new Map();
    for (const a of aliases)
        aliasMap.set(a.alias, a.app_key);
    return { apps, domains, aliases: aliasMap };
}
export function resolveRollupKey(index, keyType, keyValue) {
    if (keyType === 'domain') {
        for (const k of domainLookupKeys(keyValue)) {
            const match = index.domains.get(k);
            if (match)
                return { key_type: 'domain', key_value: keyValue, productivity: match.productivity, match };
        }
        return { key_type: 'domain', key_value: keyValue, productivity: 'undefined', match: null };
    }
    const canonical = index.aliases.get(keyValue) ?? keyValue;
    if (isBrowserAppKey(canonical))
        return { key_type: 'app', key_value: canonical, productivity: 'undefined', match: null };
    const match = index.apps.get(canonical) ?? null;
    return { key_type: 'app', key_value: canonical, productivity: match?.productivity ?? 'undefined', match };
}
/** The status of one Activity Log row (ClickUp 14zf25zjet4).
 *
 *  Only desktop `foreground_app` rows are classified (86d4a9r1f, decision of
 *  2026-10-08), so every other row is `null`: "Undefined" means "nobody has
 *  classified this yet", which an idle or heartbeat row never can be. A
 *  foreground row with neither an app nor an http(s) URL is a real moment that
 *  cannot be classified, so it is Undefined rather than null.
 *
 *  The caller must pass the FULL row: `url`, `executable` and `window_title`
 *  decide the key and are exactly what an officer's redaction nulls. */
export function resolveEventProductivity(index, row) {
    if (row.source !== 'desktop' || row.event_type !== 'foreground_app')
        return null;
    const key = resolveEventKey(row);
    if (!key)
        return 'undefined';
    return resolveRollupKey(index, key.key_type, key.key_value).productivity;
}
//# sourceMappingURL=activity-classification.js.map